import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/apiService';

interface TopUpRecord {
  id: number;
  paymentId: string;
  fundingReference: string | null;
  targetUserId: number | null;
  amountInMinor: number | null;
  currency: string | null;
  paymentStatus: string;
  walletCreditStatus: string;
  creditFailureReason: string | null;
  purpose: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PoolUserRow {
  userId: number;
  walletAvailableUsd: number;
  walletTotalUsd: number;
  allocationAmountUsd: number;
  totalPnlUsd: number;
  sharePercent: number;
  truelayerFunding: {
    topUpCount: number;
    totalFundedMajor: number;
    currency: string;
  } | null;
}

interface StrategyPool {
  strategyId: number;
  strategyName: string;
  strategyStatus: string;
  enrolledUsers: number;
  poolUsd: number;
  users: PoolUserRow[];
}

interface PoolSummary {
  totalFiatFundedMajor: number;
  totalPoolUsd: number;
  totalEnrolledUsers: number;
  pendingCreditCount: number;
  pendingCreditAmountMinor: number;
  strategyBreakdown: StrategyPool[];
  generatedAt: string;
}

type CreditFilter = 'ALL' | 'PENDING' | 'CREDITED' | 'FAILED';

const CREDIT_COLORS: Record<string, string> = {
  CREDITED: '#0dca9d',
  FAILED:   '#ff7171',
  PENDING:  '#fbb445',
};
const PAYMENT_COLORS: Record<string, string> = {
  settled:   '#0dca9d',
  executed:  '#086afb',
  failed:    '#ff7171',
  authorizing: '#fbb445',
  authorized:  '#fbb445',
};

const AdminTrueLayerTopUp: React.FC = () => {
  const [records, setRecords]             = useState<TopUpRecord[]>([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [page, setPage]                   = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filter, setFilter]               = useState<CreditFilter>('ALL');
  const [creditingId, setCreditingId]     = useState<number | null>(null);
  const [creditFxRate, setCreditFxRate]   = useState<string>('1.0');
  const [creditConfirmId, setCreditConfirmId] = useState<number | null>(null);
  const [creditResult, setCreditResult]   = useState<{ id: number; success: boolean; message: string } | null>(null);

  // Sync status state
  const [syncing, setSyncing]                 = useState(false);
  const [syncResult, setSyncResult]           = useState<{ updated: number; errors: number } | null>(null);

  // Pool summary state
  const [poolSummary, setPoolSummary]         = useState<PoolSummary | null>(null);
  const [poolLoading, setPoolLoading]         = useState(false);
  const [poolError, setPoolError]             = useState<string | null>(null);
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(null);
  const [showPoolSummary, setShowPoolSummary] = useState(true);

  const fetchPoolSummary = useCallback(async () => {
    setPoolLoading(true);
    setPoolError(null);
    try {
      const res = await apiService.get('/api/admin/truelayer-topup/pool-summary');
      if (res.success) {
        // Normalise the response — ensure all array fields default to [] if missing
        const raw = res as any;
        const normalised: PoolSummary = {
          totalFiatFundedMajor:    Number(raw.totalFiatFundedMajor   ?? 0),
          totalPoolUsd:            Number(raw.totalPoolUsd           ?? 0),
          totalEnrolledUsers:      Number(raw.totalEnrolledUsers     ?? 0),
          pendingCreditCount:      Number(raw.pendingCreditCount     ?? 0),
          pendingCreditAmountMinor:Number(raw.pendingCreditAmountMinor ?? 0),
          strategyBreakdown:       Array.isArray(raw.strategyBreakdown) ? raw.strategyBreakdown : [],
          generatedAt:             raw.generatedAt ?? new Date().toISOString(),
        };
        setPoolSummary(normalised);
      } else {
        const errMsg = (res as any).message || '';
        if ((res as any).status === 403 || errMsg.toLowerCase().includes('access') || errMsg.toLowerCase().includes('forbidden')) {
          setPoolError('Access denied — your account needs the VIEW_TRUELAYER_TOPUP permission to see this data.');
        } else {
          setPoolError(errMsg || 'Failed to load pool summary');
        }
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 403) {
        setPoolError('Access denied — your account needs the VIEW_TRUELAYER_TOPUP permission to see this data.');
      } else {
        setPoolError(err?.message || 'Network error loading pool summary');
      }
    } finally {
      setPoolLoading(false);
    }
  }, []);

  const fetchRecords = useCallback(async (p = 0, f: CreditFilter = 'ALL') => {
    setLoading(true);
    setError(null);
    try {
      const creditStatusParam = f !== 'ALL' ? `&creditStatus=${f}` : '';
      const res = await apiService.get(
        `/api/admin/truelayer-topup/events?page=${p}&limit=50${creditStatusParam}`
      );
      if (res.success) {
        const rows: TopUpRecord[] = Array.isArray(res.data) ? res.data : [];
        setRecords(rows);
        setPage((res as any).page ?? 0);
        setTotalPages((res as any).totalPages ?? 1);
        setTotalElements((res as any).totalElements ?? rows.length);
      } else {
        setError((res as any).message || 'Failed to load top-up events');
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 403) {
        setError('Access denied — your account needs the VIEW_TRUELAYER_TOPUP permission.');
      } else {
        setError(err?.message || 'Network error');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords(0, filter);
    fetchPoolSummary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSyncStatuses = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await apiService.post('/api/admin/truelayer-topup/sync-statuses', {});
      const updated = (res as any).updatedCount ?? 0;
      const errors  = (res as any).errorCount  ?? 0;
      setSyncResult({ updated, errors });
      // Reload table to show fresh statuses
      await fetchRecords(0, filter);
    } catch (err: any) {
      setSyncResult({ updated: 0, errors: 1 });
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (f: CreditFilter) => {
    setFilter(f);
    setPage(0);
    fetchRecords(0, f);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    fetchRecords(p, filter);
  };

  const handleManualCredit = async (record: TopUpRecord) => {
    setCreditingId(record.id);
    setCreditResult(null);
    try {
      const res = await apiService.post(`/api/admin/truelayer-topup/events/${record.id}/credit`, {
        fxRateToUsd: parseFloat(creditFxRate) || 1.0
      });
      if (res.success) {
        setCreditResult({ id: record.id, success: true, message: 'Wallet credited successfully.' });
        // Refresh the record in the list
        setRecords(prev => prev.map(r => r.id === record.id
          ? { ...r, walletCreditStatus: 'CREDITED' }
          : r
        ));
      } else {
        setCreditResult({ id: record.id, success: false, message: res.message || 'Credit failed.' });
      }
    } catch (err: any) {
      setCreditResult({ id: record.id, success: false, message: err?.message || 'Request failed.' });
    } finally {
      setCreditingId(null);
      setCreditConfirmId(null);
    }
  };

  const formatMajor = (minor: number | null, currency: string | null) => {
    if (minor == null) return '—';
    const major = (minor / 100).toFixed(2);
    return currency ? `${major} ${currency}` : major;
  };

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  const statusBadge = (status: string, colorMap: Record<string, string>, fallback = '#6b7280') => {
    const color = colorMap[status?.toLowerCase()] ?? colorMap[status] ?? fallback;
    return (
      <span style={{
        padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
        background: `${color}20`, color, textTransform: 'capitalize', whiteSpace: 'nowrap'
      }}>
        {status || '—'}
      </span>
    );
  };

  const pendingSettledCount = records.filter(
    r => r.walletCreditStatus === 'PENDING' &&
        (r.paymentStatus === 'settled' || r.paymentStatus === 'executed')
  ).length;

  const hasPermissionError = (error?.includes('VIEW_TRUELAYER_TOPUP') || poolError?.includes('VIEW_TRUELAYER_TOPUP'));

  if (hasPermissionError) {
    return (
      <div style={{
        fontFamily: 'Nunito, sans-serif', padding: 40, textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-block', padding: '32px 40px', background: '#fff7ed',
          border: '1px solid #fed7aa', borderRadius: 12, maxWidth: 480
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <h3 style={{ margin: '0 0 8px', color: '#92400e', fontSize: 16, fontWeight: 700 }}>
            Permission Required
          </h3>
          <p style={{ margin: '0 0 16px', color: '#b45309', fontSize: 13, lineHeight: 1.6 }}>
            Your account does not have the <code style={{
              background: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontSize: 12
            }}>VIEW_TRUELAYER_TOPUP</code> permission needed to access this page.
          </p>
          <p style={{ margin: 0, color: '#78716c', fontSize: 12 }}>
            Ask a Super Admin to run:<br />
            <code style={{
              display: 'block', marginTop: 8, padding: '8px 12px', background: '#f5f5f4',
              borderRadius: 6, fontSize: 11, textAlign: 'left', lineHeight: 1.7
            }}>
              INSERT INTO admin_permissions (admin_user_id, permission)<br />
              VALUES (&lt;your_user_id&gt;, 'VIEW_TRUELAYER_TOPUP');
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Nunito, sans-serif', padding: 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
          TrueLayer → KuCoin Wallet Top-Ups
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
          All fiat top-up payments initiated via TrueLayer for KuCoin trading wallets.
          Settled payments with PENDING wallet credit status require manual intervention.
        </p>
      </div>

      {/* ── Pool Summary Panel ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        {/* Panel header / toggle */}
        <div
          onClick={() => setShowPoolSummary(v => !v)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: '#f8faff', cursor: 'pointer',
            borderBottom: showPoolSummary ? '1px solid #e5e7eb' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>📊 Pool Summary</span>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>
              How much has been funded and which bots it backs
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={e => { e.stopPropagation(); fetchPoolSummary(); }}
              disabled={poolLoading}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', color: '#374151'
              }}
            >
              {poolLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{showPoolSummary ? '▲' : '▼'}</span>
          </div>
        </div>

        {showPoolSummary && (
          <div style={{ padding: 16 }}>
            {poolError && (
              <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                {poolError}
              </div>
            )}

            {poolLoading && !poolSummary && (
              <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>Loading pool data…</div>
            )}

            {poolSummary && (
              <>
                {/* Platform-level stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
                  {[
                    {
                      label: 'Total Fiat Funded (Credited)',
                      value: `€${Number(poolSummary.totalFiatFundedMajor || 0).toFixed(2)}`,
                      sub: 'Via TrueLayer bank transfers',
                      color: '#086afb'
                    },
                    {
                      label: 'Total Internal Pool',
                      value: `$${Number(poolSummary.totalPoolUsd || 0).toFixed(2)} USD`,
                      sub: 'Sum of all user wallet balances',
                      color: '#0dca9d'
                    },
                    {
                      label: 'Enrolled Users',
                      value: String(poolSummary.totalEnrolledUsers || 0),
                      sub: 'Active strategy allocations',
                      color: '#6366f1'
                    },
                    {
                      label: 'Pending Credits',
                      value: String(poolSummary.pendingCreditCount || 0),
                      sub: 'Settled but not yet credited',
                      color: poolSummary.pendingCreditCount > 0 ? '#f59e0b' : '#9ca3af'
                    },
                  ].map(card => (
                    <div key={card.label} style={{
                      background: 'white', border: '1px solid #e5e7eb',
                      borderRadius: 8, padding: '14px 16px'
                    }}>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{card.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{card.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Key message */}
                <div style={{
                  padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd',
                  borderRadius: 8, fontSize: 12, color: '#0369a1', marginBottom: 16
                }}>
                  <strong>How to read this:</strong> "Total Fiat Funded" = real money received via bank transfer.
                  "Total Internal Pool" = the sum credited to users' internal trading wallets (in USD).
                  These back your KuCoin bots proportionally — the pool does <em>not</em> automatically transfer to KuCoin yet (EPIC-3 pending).
                </div>

                {/* Per-strategy breakdown */}
                {(poolSummary.strategyBreakdown ?? []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13, background: '#f9fafb', borderRadius: 8 }}>
                    No active strategy enrollments found. Users need to enrol in a trading strategy.
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
                      Per-Strategy Pool Breakdown
                    </div>
                    {(poolSummary.strategyBreakdown ?? []).map(strategy => (
                      <div key={strategy.strategyId} style={{
                        border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden'
                      }}>
                        {/* Strategy row header */}
                        <div
                          onClick={() => setExpandedStrategy(expandedStrategy === strategy.strategyId ? null : strategy.strategyId)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 16px', background: '#fafafa', cursor: 'pointer',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>
                              {strategy.strategyName}
                            </span>
                            <span style={{
                              padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                              background: strategy.strategyStatus === 'RUNNING' ? '#d1fae5' : '#f3f4f6',
                              color: strategy.strategyStatus === 'RUNNING' ? '#065f46' : '#6b7280'
                            }}>
                              {strategy.strategyStatus}
                            </span>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {strategy.enrolledUsers} user{strategy.enrolledUsers !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 16, fontWeight: 800, color: '#0dca9d' }}>
                                ${Number(strategy.poolUsd || 0).toFixed(2)}
                              </div>
                              <div style={{ fontSize: 10, color: '#9ca3af' }}>pool USD</div>
                            </div>
                            <span style={{ fontSize: 12, color: '#9ca3af' }}>
                              {expandedStrategy === strategy.strategyId ? '▲ Hide users' : '▼ Show users'}
                            </span>
                          </div>
                        </div>

                        {/* Per-user breakdown (expanded) */}
                        {expandedStrategy === strategy.strategyId && (
                          <div style={{ padding: '0 0 4px' }}>
                            {strategy.users.length === 0 ? (
                              <div style={{ padding: '12px 16px', fontSize: 12, color: '#9ca3af' }}>No user data available.</div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                                    {['User ID', 'Funded via TrueLayer', 'Wallet Balance (USD)', 'Pool Share %', 'Total P&L (USD)'].map(h => (
                                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {strategy.users.map((user, idx) => {
                                    const funded = user.truelayerFunding;
                                    const pnlPositive = user.totalPnlUsd >= 0;
                                    return (
                                      <tr key={user.userId || idx} style={{
                                        borderBottom: '1px solid #f3f4f6',
                                        background: idx % 2 === 0 ? 'white' : '#fafafa'
                                      }}>
                                        <td style={{ padding: '9px 14px', fontWeight: 600, color: '#374151' }}>
                                          #{user.userId}
                                        </td>
                                        <td style={{ padding: '9px 14px' }}>
                                          {funded ? (
                                            <span>
                                              <strong style={{ color: '#086afb' }}>
                                                {funded.totalFundedMajor.toFixed(2)} {funded.currency}
                                              </strong>
                                              <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                                                ({funded.topUpCount} payment{funded.topUpCount !== 1 ? 's' : ''})
                                              </span>
                                            </span>
                                          ) : (
                                            <span style={{ color: '#9ca3af' }}>No TrueLayer top-ups</span>
                                          )}
                                        </td>
                                        <td style={{ padding: '9px 14px' }}>
                                          <div style={{ fontWeight: 700, color: '#0dca9d' }}>
                                            ${Number(user.walletAvailableUsd || 0).toFixed(2)}
                                          </div>
                                          <div style={{ fontSize: 10, color: '#9ca3af' }}>
                                            Total: ${Number(user.walletTotalUsd || 0).toFixed(2)}
                                          </div>
                                        </td>
                                        <td style={{ padding: '9px 14px' }}>
                                          <div style={{
                                            display: 'inline-block', padding: '2px 10px',
                                            background: '#f0f4ff', borderRadius: 10,
                                            fontWeight: 700, color: '#4f46e5', fontSize: 12
                                          }}>
                                            {Number(user.sharePercent || 0).toFixed(2)}%
                                          </div>
                                        </td>
                                        <td style={{ padding: '9px 14px', fontWeight: 700,
                                          color: pnlPositive ? '#059669' : '#dc2626' }}>
                                          {pnlPositive ? '+' : ''}${Number(user.totalPnlUsd || 0).toFixed(2)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, textAlign: 'right' }}>
                  Last refreshed: {poolSummary.generatedAt ? new Date(poolSummary.generatedAt).toLocaleString() : '—'}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {/* ── End Pool Summary Panel ─────────────────────────────────────── */}

      {/* Alert banner — pending records needing action */}
      {pendingSettledCount > 0 && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: 8,
          background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', gap: 10
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
            {pendingSettledCount} settled payment{pendingSettledCount > 1 ? 's' : ''} with PENDING wallet credit status.
            Use the Credit button below to manually credit them.
          </span>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: 6 }}>
          {(['ALL', 'PENDING', 'CREDITED', 'FAILED'] as CreditFilter[]).map(f => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${filter === f ? '#086afb' : '#e5e7eb'}`,
                background: filter === f ? '#086afb' : 'white',
                color: filter === f ? 'white' : '#374151',
                cursor: 'pointer', transition: 'all 0.15s'
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {/* FX Rate input for manual credit */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Manual Credit FX Rate (to USD):</label>
          <input
            type="number"
            value={creditFxRate}
            onChange={e => setCreditFxRate(e.target.value)}
            step="0.01"
            min="0.01"
            style={{
              width: 80, padding: '5px 8px', border: '1px solid #e5e7eb',
              borderRadius: 6, fontSize: 12
            }}
          />
          <button
            onClick={() => fetchRecords(page, filter)}
            disabled={loading}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: 'none', background: loading ? '#9ca3af' : '#086afb',
              color: 'white', cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={handleSyncStatuses}
            disabled={syncing || loading}
            title="Poll TrueLayer's API to refresh payment statuses for all non-terminal records"
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: 'none',
              background: syncing ? '#9ca3af' : '#0dca9d',
              color: 'white', cursor: (syncing || loading) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5
            }}
          >
            {syncing ? '⏳ Syncing…' : '🔄 Sync from TrueLayer'}
          </button>
        </div>
      </div>

      {/* Sync result banner */}
      {syncResult && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10,
          background: syncResult.errors > 0 ? '#fef3c7' : '#d1fae5',
          color: syncResult.errors > 0 ? '#92400e' : '#065f46',
          border: `1px solid ${syncResult.errors > 0 ? '#fcd34d' : '#6ee7b7'}`
        }}>
          Sync complete — <strong>{syncResult.updated}</strong> record(s) updated from TrueLayer.
          {syncResult.errors > 0 && <span> ⚠️ {syncResult.errors} error(s) — check backend logs.</span>}
          <button onClick={() => setSyncResult(null)} style={{
            marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, color: 'inherit', fontWeight: 700
          }}>✕</button>
        </div>
      )}

      {/* Summary count */}
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        Showing {records.length} of {totalElements} records
        {filter !== 'ALL' && ` (filtered: ${filter})`}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Credit result toast */}
      {creditResult && (
        <div style={{
          padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13,
          background: creditResult.success ? '#d1fae5' : '#fee2e2',
          color: creditResult.success ? '#065f46' : '#991b1b',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>{creditResult.message}</span>
          <button onClick={() => setCreditResult(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}>
            ×
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading top-up events…</div>
      ) : records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', color: '#6b7280', fontSize: 13 }}>
          No top-up records found{filter !== 'ALL' ? ` for status: ${filter}` : ''}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                {['ID', 'User ID', 'Date', 'Reference', 'Amount', 'Payment Status', 'Wallet Credit', 'Action'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map(record => {
                const canCredit = record.walletCreditStatus !== 'CREDITED' &&
                  (record.paymentStatus === 'settled' || record.paymentStatus === 'executed');
                const isConfirming = creditConfirmId === record.id;
                const isCrediting  = creditingId === record.id;

                return (
                  <tr key={record.id} style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: canCredit && record.walletCreditStatus === 'PENDING' ? '#fffbeb' : 'white'
                  }}>
                    <td style={{ padding: '10px 12px', color: '#6b7280', fontFamily: 'monospace' }}>{record.id}</td>
                    <td style={{ padding: '10px 12px', color: '#374151', fontWeight: 600 }}>{record.targetUserId ?? '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(record.createdAt)}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280', fontFamily: 'monospace', fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span title={record.fundingReference || record.paymentId}>
                        {record.fundingReference || (record.paymentId?.substring(0, 20) + '…')}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#086afb', whiteSpace: 'nowrap' }}>
                      {formatMajor(record.amountInMinor, record.currency)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {statusBadge(record.paymentStatus, PAYMENT_COLORS)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {statusBadge(record.walletCreditStatus, CREDIT_COLORS)}
                      {record.walletCreditStatus === 'FAILED' && record.creditFailureReason && (
                        <div style={{
                          fontSize: 10, color: '#b91c1c', marginTop: 3, maxWidth: 180,
                          whiteSpace: 'normal', lineHeight: 1.3
                        }}
                          title={record.creditFailureReason}
                        >
                          {record.creditFailureReason.length > 60
                            ? record.creditFailureReason.substring(0, 60) + '…'
                            : record.creditFailureReason}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      {canCredit ? (
                        isConfirming ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: '#92400e' }}>Credit @ {creditFxRate}x?</span>
                            <button
                              onClick={() => handleManualCredit(record)}
                              disabled={isCrediting}
                              style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: '#0dca9d', color: 'white', fontSize: 11, fontWeight: 600, cursor: isCrediting ? 'not-allowed' : 'pointer' }}
                            >
                              {isCrediting ? '…' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setCreditConfirmId(null)}
                              style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: 'white', fontSize: 11, cursor: 'pointer' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setCreditConfirmId(record.id); setCreditResult(null); }}
                            style={{
                              padding: '4px 12px', borderRadius: 6, border: 'none',
                              background: '#086afb', color: 'white', fontSize: 11,
                              fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Credit
                          </button>
                        )
                      ) : (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          {record.walletCreditStatus === 'CREDITED' ? 'Done' : 'N/A'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            disabled={page === 0}
            onClick={() => handlePageChange(page - 1)}
            style={{ padding: '6px 16px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: page === 0 ? 'not-allowed' : 'pointer', background: 'white', fontSize: 12 }}
          >
            ‹ Prev
          </button>
          <span style={{ fontSize: 12, color: '#6b7280', padding: '6px 10px' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => handlePageChange(page + 1)}
            style={{ padding: '6px 16px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', background: 'white', fontSize: 12 }}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTrueLayerTopUp;
